import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Swords, Search, Circle, AlertCircle, Loader2, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { GameIcon } from '@/components/ui/GameIcon';
import { GAMES, getGameName } from '@/lib/gameConfig';
import { cn } from '@/lib/utils';
import { api, type LobbyMember, type PresenceStatus } from '@/lib/api';
import { useLobby } from '@/hooks/useSocket';
import { useAuthStore } from '@/stores/auth.store';

// Deduplicated games for the arena switcher
const arenaGames = GAMES.filter((g, i, arr) =>
  arr.findIndex((x) => x.name === g.name) === i
);

const statusColors: Record<PresenceStatus, string> = {
  online: 'bg-green-500',
  away: 'bg-blue-400',
  looking_for_match: 'bg-purple-500',
  in_match: 'bg-yellow-500',
  offline: 'bg-gray-500',
};

const statusLabels: Record<PresenceStatus, string> = {
  online: 'Online',
  away: 'Away',
  looking_for_match: 'Looking for Match',
  in_match: 'In Match',
  offline: 'Offline',
};

export function LobbyPage() {
  const { game } = useParams<{ game: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isAuthenticated } = useAuthStore();
  const [search, setSearch] = useState('');

  // Use real-time lobby subscription for presence updates
  const { members: realtimeMembers, isConnected } = useLobby(game || '');

  // Fetch initial lobby data from REST API
  const {
    data: lobby,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['lobby', game],
    queryFn: () => api.getLobby(game || ''),
    enabled: !!game,
    staleTime: 30000,
    refetchOnWindowFocus: true,
  });

  // Fetch open challenges for this game
  const { data: openWagers } = useQuery({
    queryKey: ['wagers', 'open', game],
    queryFn: () => api.getWagers({ gameCode: game, status: 'open' }),
    enabled: !!game,
    staleTime: 15000,
  });

  // Join lobby mutation
  const joinLobbyMutation = useMutation({
    mutationFn: () => api.joinLobby(game || ''),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lobby', game] });
    },
  });

  // Send challenge mutation
  const sendChallengeMutation = useMutation({
    mutationFn: (challengedId: string) =>
      api.sendChallenge({
        challengedId,
        gameCode: game || '',
        amount: '10',
        challengeType: 'quick',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['challenges'] });
    },
  });

  // Auto-join lobby when authenticated and lobby loads
  useEffect(() => {
    if (isAuthenticated && game && lobby) {
      const isInLobby = lobby.members.some((m) => m.id === user?.id);
      if (!isInLobby && !joinLobbyMutation.isPending) {
        joinLobbyMutation.mutate();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, game, lobby, user?.id]);

  // Merge API members with real-time presence updates
  const mergedMembers = useCallback((): LobbyMember[] => {
    if (!lobby?.members) return [];

    return lobby.members.map((member) => {
      const realtimeMember = realtimeMembers.find((rm) => rm.userId === member.id);

      if (realtimeMember) {
        return {
          ...member,
          presence: {
            ...member.presence,
            status: realtimeMember.status,
          },
        };
      }

      return member;
    });
  }, [lobby?.members, realtimeMembers]);

  const members = mergedMembers();
  const onlineCount = members.filter(
    (m) => m.presence.status === 'online' || m.presence.status === 'looking_for_match'
  ).length;

  const filteredMembers = members.filter(
    (m) =>
      !search ||
      m.displayName.toLowerCase().includes(search.toLowerCase()) ||
      m.gameAccount.username.toLowerCase().includes(search.toLowerCase())
  );

  // Sort members: online first, then by rating
  const sortedMembers = [...filteredMembers].sort((a, b) => {
    const statusOrder: Record<PresenceStatus, number> = {
      looking_for_match: 0,
      online: 1,
      away: 2,
      in_match: 3,
      offline: 4,
    };
    const statusDiff = statusOrder[a.presence.status] - statusOrder[b.presence.status];
    if (statusDiff !== 0) return statusDiff;

    return (b.gameAccount.rating || 0) - (a.gameAccount.rating || 0);
  });

  const handleChallenge = (memberId: string) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    sendChallengeMutation.mutate(memberId);
  };

  const handleCreateWager = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    navigate(`/wagers/create?game=${game}`);
  };

  const handleAcceptWager = (wagerId: string) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    navigate(`/wagers/${wagerId}`);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading arena...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="flex flex-col items-center gap-4 p-6">
            <AlertCircle className="h-12 w-12 text-destructive" />
            <div className="text-center">
              <h2 className="text-lg font-semibold">Failed to Load Arena</h2>
              <p className="text-sm text-muted-foreground">
                {error instanceof Error ? error.message : 'An error occurred'}
              </p>
            </div>
            <Button onClick={() => refetch()}>Try Again</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20 lg:pb-0">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <GameIcon gameCode={game || 'chess_com'} size="lg" />
          <div>
            <h1 className="font-heading text-2xl font-bold">{getGameName(game || '')} Arena</h1>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Users className="h-4 w-4" />
              <span>
                {onlineCount} online{' '}
                {isConnected && <Circle className="ml-1 inline h-2 w-2 fill-green-500 text-green-500" />}
              </span>
              <span className="text-muted-foreground/50">|</span>
              <span>{lobby?.memberCount || members.length} members</span>
            </div>
          </div>
        </div>
        <Button variant="gradient" className="gap-2" onClick={handleCreateWager}>
          <Swords className="h-4 w-4" />
          New Challenge
        </Button>
      </div>

      {/* Game Switcher */}
      <div className="relative">
        <select
          value={game || ''}
          onChange={(e) => navigate(`/lobbies/${e.target.value}`)}
          className="w-full appearance-none rounded-xl border border-border bg-card px-4 py-3 pr-10 text-sm font-medium text-foreground transition-colors focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          {arenaGames.map((g) => (
            <option key={g.code} value={g.code}>
              {g.name}
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search players..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      {/* Member List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Circle className="h-2 w-2 animate-pulse fill-green-500 text-green-500" />
            Online Now ({onlineCount})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {sortedMembers.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              {search ? 'No players match your search' : 'No players online'}
            </div>
          ) : (
            <AnimatePresence mode="popLayout">
              {sortedMembers.map((member, i) => (
                <motion.div
                  key={member.id}
                  layout
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ delay: i * 0.03 }}
                  className={cn(
                    'flex items-center gap-4 rounded-xl border p-4 transition-colors',
                    member.presence.status === 'in_match' && 'opacity-60',
                    member.id === user?.id && 'border-primary/30 bg-primary/5'
                  )}
                >
                  <div className="relative">
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={member.avatarUrl || undefined} />
                      <AvatarFallback>{member.displayName[0]}</AvatarFallback>
                    </Avatar>
                    <div
                      className={cn(
                        'absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full border-2 border-background',
                        statusColors[member.presence.status]
                      )}
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium truncate">
                        {member.displayName}
                        {member.id === user?.id && (
                          <span className="ml-1 text-xs text-muted-foreground">(you)</span>
                        )}
                      </p>
                      {member.gameAccount.rating && (
                        <Badge variant="secondary" className="text-xs shrink-0">
                          {member.gameAccount.rating}
                        </Badge>
                      )}
                      {member.gameAccount.rank && (
                        <Badge variant="outline" className="text-xs shrink-0">
                          {member.gameAccount.rank}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">
                      @{member.gameAccount.username}
                      {member.stats && (
                        <span className="ml-2">
                          {Math.round(member.stats.winRate * 100)}% win rate
                        </span>
                      )}
                      <span className="ml-2 hidden sm:inline">
                        {statusLabels[member.presence.status]}
                      </span>
                    </p>
                  </div>

                  {member.id !== user?.id && (
                    <>
                      {member.presence.status !== 'in_match' &&
                      member.presence.status !== 'offline' ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-2 shrink-0"
                          onClick={() => handleChallenge(member.id)}
                          disabled={sendChallengeMutation.isPending}
                        >
                          {sendChallengeMutation.isPending &&
                          sendChallengeMutation.variables === member.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Swords className="h-4 w-4" />
                          )}
                          <span className="hidden sm:inline">Challenge</span>
                        </Button>
                      ) : (
                        <Badge variant="secondary" className="shrink-0">
                          {member.presence.status === 'in_match' ? 'In Match' : 'Offline'}
                        </Badge>
                      )}
                    </>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          )}
        </CardContent>
      </Card>

      {/* Open Challenges in Arena */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            Open Challenges
            <Badge variant="secondary">{openWagers?.length || 0}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {!openWagers || openWagers.length === 0 ? (
            <div className="py-6 text-center text-muted-foreground">
              No open challenges. Be the first to create one!
            </div>
          ) : (
            openWagers.slice(0, 5).map((wager, i) => (
              <motion.div
                key={wager.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }}
                className="flex items-center justify-between rounded-xl border p-4"
              >
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-primary/10 px-3 py-1.5">
                    <span className="font-heading text-lg font-bold text-primary">
                      ${wager.amount}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium">{wager.wagerType}</p>
                    <p className="text-sm text-muted-foreground">
                      @{wager.creator.displayName}
                    </p>
                  </div>
                </div>
                {wager.creator.id !== user?.id && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => handleAcceptWager(wager.id)}
                  >
                    Accept
                  </Button>
                )}
              </motion.div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Challenge notification toast would go here via a toast system */}
      {sendChallengeMutation.isSuccess && (
        <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-4">
          <Card className="border-green-500/50 bg-green-50 dark:bg-green-950">
            <CardContent className="flex items-center gap-2 p-4">
              <Circle className="h-2 w-2 fill-green-500 text-green-500" />
              <span className="text-sm">Challenge sent!</span>
            </CardContent>
          </Card>
        </div>
      )}

      {sendChallengeMutation.isError && (
        <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-4">
          <Card className="border-destructive/50 bg-destructive/10">
            <CardContent className="flex items-center gap-2 p-4">
              <AlertCircle className="h-4 w-4 text-destructive" />
              <span className="text-sm">
                {sendChallengeMutation.error instanceof Error
                  ? sendChallengeMutation.error.message
                  : 'Failed to send challenge'}
              </span>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
